import React, { useId, useState } from 'react';
import { isReservedPropertyKey } from './frontmatter-yaml';
import {
  normalizeKeyInput,
  PROPERTY_TYPES,
  type PropertyType,
} from './property-values';

const FIELD =
  'rounded-md border border-border bg-transparent px-2 py-1 text-sm outline-none focus:border-ring';

export interface NewProperty {
  key: string;
  type: PropertyType;
}

export interface AddPropertyFormProps {
  existingKeys: readonly string[];
  onAdd: (property: NewProperty) => void;
  onClose: () => void;
}

export function AddPropertyForm({
  existingKeys,
  onAdd,
  onClose,
}: AddPropertyFormProps) {
  const [rawKey, setRawKey] = useState('');
  const [chosenType, setChosenType] = useState<PropertyType>('text');
  const listId = useId();

  const key = normalizeKeyInput(rawKey);
  const duplicate =
    key !== null &&
    (existingKeys.includes(key) || isReservedPropertyKey(key));
  const reserved = key !== null && isReservedPropertyKey(key);

  function submit() {
    if (!key || duplicate) {
      return;
    }
    onAdd({ key, type: chosenType });
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLElement>) {
    if (event.key === 'Enter') {
      if (event.target instanceof HTMLButtonElement) {
        return;
      }
      event.preventDefault();
      submit();
    } else if (event.key === 'Escape') {
      event.stopPropagation();
      onClose();
    }
  }

  return (
    <div
      role="group"
      aria-label={t.app.properties.add}
      className="mt-1 flex flex-wrap items-center gap-2 rounded-md bg-muted/50 p-2"
      onKeyDown={handleKeyDown}
    >
      <input
        autoFocus
        list={listId}
        value={rawKey}
        onChange={(e) => setRawKey(e.target.value)}
        placeholder={t.app.properties.keyPlaceholder}
        aria-label={t.app.properties.keyLabel}
        aria-invalid={duplicate || undefined}
        title={
          reserved
            ? t.app.properties.reservedKey
            : duplicate
              ? t.app.properties.duplicateKey
              : undefined
        }
        maxLength={100}
        className={`${FIELD} w-44 min-w-0`}
      />
      <select
        aria-label={t.app.properties.typeLabel}
        value={chosenType}
        onChange={(e) => setChosenType(e.target.value as PropertyType)}
        className={FIELD}
      >
        {PROPERTY_TYPES.map((option) => (
          <option key={option} value={option}>
            {t.app.properties.types[option]}
          </option>
        ))}
      </select>
      <div className="ml-auto flex items-center gap-1">
        <button
          type="button"
          onClick={onClose}
          className="rounded-md px-2.5 py-1 text-sm hover:bg-muted"
        >
          {t.app.common.cancelButton}
        </button>
        <button
          type="button"
          disabled={!key || duplicate}
          onClick={submit}
          className="rounded-md bg-foreground px-2.5 py-1 text-sm text-background disabled:opacity-50"
        >
          {t.app.properties.addAction}
        </button>
      </div>
    </div>
  );
}
